import { useEffect, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CalendarDays,
  CarFront,
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  CloudSun,
  ExternalLink,
  FishSymbol,
  ListChecks,
  Map,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  TicketCheck,
  Trash2,
  X,
} from "lucide-react";
import type { PublicPermitProduct } from "@natural/public-contract";
import {
  accountApi,
  consumerActivityLabels,
  type ConsumerMe,
  type FishingTrip,
  type FishingTripEnvironment,
  type FishingTripWorkspace,
} from "../../account";
import {
  TripDestinationWeatherComparison,
  TripWeatherPanel,
} from "../components/TripWeatherPanel";
import { TripEnvironmentPanel } from "../components/TripEnvironmentPanel";
import { TripUnifiedTimeline } from "../components/TripUnifiedTimeline";
import { TripDepartureSummary } from "../components/TripDepartureSummary";
import { TripTacklePacking } from "../components/TripTacklePacking";
import { TripFishingJournal } from "../components/TripFishingJournal";

export function ConsumerTripDetail({
  trip,
  me,
  products,
}: {
  trip: FishingTrip;
  me: ConsumerMe;
  products: PublicPermitProduct[];
}) {
  const [workspace, setWorkspace] = useState<FishingTripWorkspace | null>(null),
    [state, setState] = useState("Ladataan reissusuunnitelmaa…"),
    [tab, setTab] = useState<
      | "summary"
      | "timeline"
      | "permits"
      | "places"
      | "packing"
      | "journal"
      | "tasks"
    >((new URLSearchParams(window.location.search).get("tab") === "journal" ||
      new URLSearchParams(window.location.search).get("result") === "1")
      ? "journal"
      : "summary"),
    [suggestions, setSuggestions] = useState<Array<Record<string, unknown>>>(
      [],
    ),
    [suggestionBoundary, setSuggestionBoundary] = useState(""),
    [message, setMessage] = useState(""),
    [climate, setClimate] =
      useState<FishingTripWorkspace["weather"]["seasonalContext"]>(null),
    [environment, setEnvironment] = useState<FishingTripEnvironment | null>(
      null,
    ),
    [environmentError, setEnvironmentError] = useState(false),
    [editing, setEditing] = useState(
      new URLSearchParams(window.location.search).get("edit") === "1",
    );
  const load = () =>
    accountApi<FishingTripWorkspace>(
      `/api/account/trips/${encodeURIComponent(trip.id)}`,
    )
      .then((value) => {
        setWorkspace(value);
        setState("");
      })
      .catch((error) =>
        setState(
          error instanceof Error ? error.message : "Reissua ei voitu ladata.",
        ),
      );
  useEffect(() => {
    void load();
  }, [trip.id]);
  useEffect(() => {
    setEnvironment(null);
    setEnvironmentError(false);
    void accountApi<FishingTripEnvironment>(
      `/api/account/trips/${encodeURIComponent(trip.id)}/environment`,
    )
      .then(setEnvironment)
      .catch(() => setEnvironmentError(true));
  }, [trip.id]);
  useEffect(() => {
    if (workspace?.weather.state !== "forecast_pending" || climate) return;
    void accountApi<{
      seasonalContext: FishingTripWorkspace["weather"]["seasonalContext"];
    }>(`/api/account/trips/${encodeURIComponent(trip.id)}/climate`)
      .then((value) => setClimate(value.seasonalContext))
      .catch(() => undefined);
  }, [workspace?.weather.state, trip.id]);
  const refresh = async () => {
    setMessage("Tarkistetaan julkaistusta Coresta…");
    try {
      await accountApi(
        `/api/account/trips/${encodeURIComponent(trip.id)}/recheck`,
        { method: "POST", body: "{}" },
      );
      await load();
      setMessage("Lupaohje ja valmiustila päivitettiin.");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Tarkistus epäonnistui.",
      );
    }
  };
  const loadSuggestions = async () => {
    setMessage("Etsitään varakohteita…");
    try {
      const value = await accountApi<{
        suggestions: Array<Record<string, unknown>>;
        boundary: string;
      }>(
        `/api/account/trips/${encodeURIComponent(trip.id)}/alternate-suggestions`,
      );
      setSuggestions(value.suggestions);
      setSuggestionBoundary(value.boundary);
      setMessage(
        value.suggestions.length
          ? "Valitse enintään kolme varakohdetta."
          : "Sopivia varakohteita ei löytynyt nykyisestä hakemistosta.",
      );
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Varakohteita ei voitu hakea.",
      );
    }
  };
  const addDestination = async (item: Record<string, unknown>) => {
    await accountApi(
      `/api/account/trips/${encodeURIComponent(trip.id)}/destinations`,
      {
        method: "POST",
        body: JSON.stringify({
          placeProvider: item.place_provider,
          placeId: item.place_id,
        }),
      },
    );
    setSuggestions((current) =>
      current.filter((value) => value.place_id !== item.place_id),
    );
    await load();
    setMessage(
      `${String(item.place_name)} lisättiin varakohteeksi. Lupa tarkistettiin erikseen julkaistusta Coresta.`,
    );
  };
  const removeDestination = async (id: string) => {
    await accountApi(
      `/api/account/trips/${encodeURIComponent(trip.id)}/destinations/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
    await load();
  };
  const addTask = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      data = new FormData(form);
    await accountApi(
      `/api/account/trips/${encodeURIComponent(trip.id)}/tasks`,
      {
        method: "POST",
        body: JSON.stringify({
          title: data.get("title"),
          taskType: data.get("taskType"),
        }),
      },
    );
    form.reset();
    await load();
  };
  const toggleTask = async (id: string, status: string) => {
    await accountApi(
      `/api/account/trips/${encodeURIComponent(trip.id)}/tasks/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          status: ["completed", "verified", "user_reported"].includes(status)
            ? "open"
            : "completed",
        }),
      },
    );
    await load();
    setMessage(
      status === "open"
        ? "Tehtävä merkittiin tehdyksi. Järjestelmätehtävän lupa ei muutu tällä Core-varmennetuksi."
        : "Tehtävä avattiin uudelleen.",
    );
  };
  const savePlan = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setMessage("Päivitetään reissua ja tarkistetaan koko ajanjakso…");
    try {
      await accountApi(`/api/account/trips/${encodeURIComponent(trip.id)}`, {
        method: "PATCH",
        body: JSON.stringify({
          plannedDate: data.get("plannedDate"),
          endDate: data.get("endDate"),
          activity: data.get("activity"),
          rodCount: Number(data.get("rodCount")),
          targetSpecies: data.get("targetSpecies"),
          preferredStartTime: data.get("preferredStartTime"),
          preferredEndTime: data.get("preferredEndTime"),
          maxTravelMinutes: Number(data.get("maxTravelMinutes")),
          notes: data.get("notes"),
        }),
      });
      window.location.assign(`/oma/kalareissut/${encodeURIComponent(trip.id)}`);
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Reissua ei voitu päivittää.",
      );
    }
  };
  if (!workspace)
    return (
      <section className="trip-detail">
        <a className="trip-detail-back" href="/oma/kalareissut">
          ← Kaikki kalareissut
        </a>
        <div className="trip-guidance-loading">
          <RefreshCw size={20} />
          <span>
            <b>{state}</b>
            <small>Reissun vanhaa arviota ei esitetä ajantasaisena.</small>
          </span>
        </div>
      </section>
    );
  const tripHasEnded =
    workspace.trip.status === "planned" &&
    workspace.trip.end_date < new Date().toISOString().slice(0, 10);
  const primary = workspace.destinations.find(
      (item) => item.role === "primary",
    ),
    assessment = workspace.assessments.find(
      (item) => item.destination_id === primary?.id,
    ),
    spans = assessment?.summary?.spans ?? [];
  const dateLabel =
    trip.planned_date === workspace.trip.end_date
      ? new Date(`${trip.planned_date}T12:00:00`).toLocaleDateString("fi-FI", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : `${new Date(`${trip.planned_date}T12:00:00`).toLocaleDateString("fi-FI")}–${new Date(`${workspace.trip.end_date}T12:00:00`).toLocaleDateString("fi-FI")}`;
  const readiness = [
    { key: "permit", label: "Lupa", icon: ShieldCheck },
    { key: "weather", label: "Sää", icon: CloudSun },
    { key: "purchases", label: "Hankinnat", icon: TicketCheck },
    { key: "tasks", label: "Tehtävät", icon: ListChecks },
  ] as const;
  const readinessLabel = (value: string) =>
    ({
      checked: "Tarkistettu",
      attention: "Vaatii huomiota",
      blocked: "Estynyt",
      seasonal: "Kausikuva",
      forecast_available: "Ennuste saatavilla",
      past: "Reissu päättynyt",
    })[value] ?? value;
  return (
    <section className="trip-detail" aria-labelledby="trip-detail-title">
      <a className="trip-detail-back" href="/oma/kalareissut">
        ← Kaikki kalareissut
      </a>
      <header>
        <div>
          <span className="eyebrow">Elävä reissusuunnitelma</span>
          <h2 id="trip-detail-title">
            {trip.place_name}
            {trip.municipality_name ? ` · ${trip.municipality_name}` : ""}
          </h2>
          <p>
            {dateLabel} · {consumerActivityLabels[workspace.trip.activity]} ·{" "}
            {workspace.trip.rod_count} vavalla
          </p>
          <div className="trip-preference-summary">
            {workspace.trip.target_species && (
              <span>
                <FishSymbol />
                Tavoite: {workspace.trip.target_species}
              </span>
            )}
            {workspace.trip.preferred_start_time &&
              workspace.trip.preferred_end_time && (
                <span>
                  <Clock3 />
                  {workspace.trip.preferred_start_time}–
                  {workspace.trip.preferred_end_time}
                </span>
              )}
            {workspace.trip.max_travel_minutes && (
              <span>
                <CarFront />
                Enintään {workspace.trip.max_travel_minutes} min
              </span>
            )}
          </div>
        </div>
        <div className="trip-header-actions">
          <button type="button" onClick={() => setEditing((value) => !value)}>
            <Pencil size={15} />
            {editing ? "Sulje muokkaus" : "Muokkaa reissua"}
          </button>
          <button type="button" onClick={() => void refresh()}>
            <RefreshCw size={15} />
            Tarkista nyt
          </button>
        </div>
      </header>
      {message && <p className="form-message trip-message">{message}</p>}
      {tripHasEnded && (
        <section className="trip-finish-callout">
          <span><FishSymbol /></span>
          <div>
            <small>Reissun ajankohta on päättynyt</small>
            <h3>Kirjaa tulos ja päätä reissu</h3>
            <p>
              Pakkausvaraukset eivät enää sido Kalapakkia. Lisää saalis tai
              merkitse reissu saaliittomaksi päiväkirjassa.
            </p>
          </div>
          <button type="button" onClick={() => setTab("journal")}>
            Avaa päiväkirja <ArrowRight />
          </button>
        </section>
      )}
      {editing && (
        <form
          className="trip-plan-edit-form"
          onSubmit={(event) => void savePlan(event)}
        >
          <div>
            <span className="eyebrow">Muokkaa suunnitelmaa</span>
            <h3>Suunnittele koko reissu kerralla</h3>
            <p>
              Core tarkistaa jokaisen päivän uudelleen. Laji-, kellonaika- ja
              matka-asetukset ohjaavat suunnittelua, eivät muuta lupapäätöstä.
            </p>
          </div>
          <label>
            Alkaa
            <input
              name="plannedDate"
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              defaultValue={workspace.trip.planned_date}
              onChange={(event) => {
                const end = event.currentTarget.form?.elements.namedItem(
                  "endDate",
                ) as HTMLInputElement | null;
                if (end) {
                  end.min = event.currentTarget.value;
                  if (end.value < event.currentTarget.value)
                    end.value = event.currentTarget.value;
                }
              }}
              required
            />
          </label>
          <label>
            Päättyy <small>enintään 31 päivää</small>
            <input
              name="endDate"
              type="date"
              min={workspace.trip.planned_date}
              defaultValue={workspace.trip.end_date}
              required
            />
          </label>
          <label>
            Kalastustapa
            <select name="activity" defaultValue={workspace.trip.activity}>
              {Object.entries(consumerActivityLabels).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Vapojen määrä
            <input
              name="rodCount"
              type="number"
              min="1"
              max="20"
              defaultValue={workspace.trip.rod_count}
              required
            />
          </label>
          <label>
            Tavoitelaji
            <input
              name="targetSpecies"
              maxLength={80}
              defaultValue={workspace.trip.target_species ?? ""}
              placeholder="Esimerkiksi kuha"
            />
          </label>
          <label>
            Enimmäisajoaika
            <select
              name="maxTravelMinutes"
              defaultValue={workspace.trip.max_travel_minutes ?? 45}
            >
              {[15, 30, 45, 60, 90].map((value) => (
                <option value={value} key={value}>
                  {value} min
                </option>
              ))}
            </select>
          </label>
          <label>
            Aloitusaika
            <input
              name="preferredStartTime"
              type="time"
              defaultValue={workspace.trip.preferred_start_time ?? ""}
            />
          </label>
          <label>
            Lopetusaika
            <input
              name="preferredEndTime"
              type="time"
              defaultValue={workspace.trip.preferred_end_time ?? ""}
            />
          </label>
          <label className="wide-field">
            Muistiinpanot
            <textarea
              name="notes"
              maxLength={500}
              rows={3}
              defaultValue={workspace.trip.notes}
            />
          </label>
          <button>
            <RefreshCw size={15} />
            Tallenna ja tarkista koko reissu
          </button>
        </form>
      )}
      <div className="trip-readiness-grid">
        {readiness.map((item) => {
          const Icon = item.icon,
            value = workspace.readiness[item.key];
          return (
            <article className={value} key={item.key}>
              <Icon />
              <span>
                {item.label}
                <b>{readinessLabel(value)}</b>
              </span>
            </article>
          );
        })}
      </div>
      <nav className="trip-tabs" aria-label="Reissusuunnitelman osiot">
        {[
          ["summary", "Yhteenveto"],
          ["timeline", "Aikajana"],
          ["permits", "Luvat ja säännöt"],
          ["places", "Kohteet"],
          ["packing", "Pakkaus"],
          ["journal", "Päiväkirja"],
          ["tasks", "Muut valmistelut"],
        ].map(([value, label]) => (
          <button
            type="button"
            aria-current={tab === value ? "page" : undefined}
            onClick={() => setTab(value as typeof tab)}
            key={value}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "summary" && (
        <>
          <TripDepartureSummary workspace={workspace} onSelect={setTab} />
          <div className="trip-workspace-grid">
            <article
              className={`trip-guidance-result ${assessment?.decision ?? "unknown"}`}
            >
              <span>
                {assessment?.guidance_status === "resolved" ? (
                  <ShieldCheck />
                ) : (
                  <AlertTriangle />
                )}
              </span>
              <div>
                <small>Nykyisen julkaistun tiedon perusteella</small>
                <h3>{spans[0]?.title ?? "Lupaa ei voida vielä ratkaista"}</h3>
                <p>
                  {spans.length > 1
                    ? `Reissun aikana on ${spans.length} erilaista lupa- tai sääntöjaksoa. Tarkista päivät Luvat ja säännöt -osiosta.`
                    : spans[0]?.reasons.join(" ") ||
                      "Core-arvio odottaa käsittelyä."}
                </p>
                <em>
                  Tarkistettu{" "}
                  {assessment?.checked_at
                    ? new Date(assessment.checked_at).toLocaleString("fi-FI")
                    : "ei vielä"}
                </em>
              </div>
            </article>
            <TripWeatherPanel weather={workspace.weather} climate={climate} />
            <TripEnvironmentPanel
              environment={environment}
              error={environmentError}
            />
            {trip.notes && (
              <section className="trip-notes">
                <small>Omat muistiinpanot</small>
                <p>{trip.notes}</p>
              </section>
            )}
          </div>
        </>
      )}
      {tab === "timeline" && <TripUnifiedTimeline workspace={workspace} />}
      {tab === "permits" && (
        <section className="trip-permit-timeline">
          <header>
            <div>
              <span className="eyebrow">Päiväkohtainen arvio</span>
              <h3>Luvat ja säännöt koko reissulle</h3>
            </div>
            <small>Core yhdistää peräkkäiset samanlaiset päivät.</small>
          </header>
          {spans.length ? (
            spans.map((span, index) => {
              const required = span.permitProductIds.map((id) =>
                products.find((item) => item.id === id),
              );
              return (
                <article
                  className={span.decision ?? "unknown"}
                  key={`${span.startDate}-${index}`}
                >
                  <span>
                    <b>
                      {new Date(
                        `${span.startDate}T12:00:00`,
                      ).toLocaleDateString("fi-FI")}
                      {span.endDate !== span.startDate
                        ? `–${new Date(`${span.endDate}T12:00:00`).toLocaleDateString("fi-FI")}`
                        : ""}
                    </b>
                    <small>
                      {span.areaNames.join(", ") || "Aluetta ei ratkaistu"}
                    </small>
                  </span>
                  <div>
                    <h4>{span.title}</h4>
                    {span.reasons.map((reason) => (
                      <p key={reason}>{reason}</p>
                    ))}
                    {span.warnings.length > 0 && (
                      <ul>
                        {span.warnings.map((value) => (
                          <li key={value}>{value}</li>
                        ))}
                      </ul>
                    )}
                    {required.map((product, index) =>
                      product ? (
                        <a
                          href={product.salesUrl ?? "#"}
                          target={product.salesUrl ? "_blank" : undefined}
                          rel="noreferrer"
                          key={span.permitProductIds[index]}
                        >
                          {product.name}
                          {product.salesUrl && <ExternalLink />}
                        </a>
                      ) : (
                        <b key={span.permitProductIds[index]}>
                          Lupatuote {span.permitProductIds[index]}
                        </b>
                      ),
                    )}
                  </div>
                </article>
              );
            })
          ) : (
            <div className="trip-neutral-state">
              <AlertTriangle />
              <span>
                <b>Ei vielä Core-arviota</b>
                <small>
                  Käynnistä tarkistus tai odota automaattista uusintaa.
                </small>
              </span>
            </div>
          )}
        </section>
      )}
      {tab === "places" && (
        <section className="trip-places">
          <header>
            <div>
              <span className="eyebrow">Pääkohde ja varasuunnitelma</span>
              <h3>Reissun kalapaikat</h3>
            </div>
            <button
              type="button"
              onClick={() => void loadSuggestions()}
              disabled={workspace.destinations.length >= 4}
            >
              <Map size={16} />
              Ehdota varakohteita
            </button>
          </header>
          <div className="trip-destination-list">
            {workspace.destinations.map((item) => (
              <article key={item.id}>
                <MapPin />
                <div>
                  <small>
                    {item.role === "primary"
                      ? "Pääkohde"
                      : `Varakohde ${item.position}`}
                  </small>
                  <h4>{item.place_name}</h4>
                  <p>{item.municipality_name ?? "Kunta ei tiedossa"}</p>
                </div>
                {item.role === "alternate" && (
                  <button
                    aria-label={`Poista ${item.place_name}`}
                    onClick={() => void removeDestination(item.id)}
                  >
                    <X />
                  </button>
                )}
              </article>
            ))}
          </div>
          <TripDestinationWeatherComparison weather={workspace.weather} />
          {suggestionBoundary && (
            <p className="trip-suggestion-boundary">
              <CarFront />
              {suggestionBoundary}
            </p>
          )}
          {suggestions.length > 0 && (
            <div className="trip-suggestion-list">
              <h4>Matkatoiveeseen sopivat ehdokkaat</h4>
              {suggestions.map((item) => (
                <article key={String(item.place_id)}>
                  <div>
                    <b>{String(item.place_name)}</b>
                    <small>
                      {String(item.kind) === "river" ? "Joki" : "Järvi"} ·{" "}
                      {String(item.municipality_name ?? "Kunta ei tiedossa")} ·{" "}
                      {String(item.distanceKm)} km · noin{" "}
                      {String(item.estimatedTravelMinutes)} min
                    </small>
                    <em className={String(item.readiness)}>
                      {String(item.reason)}
                    </em>
                  </div>
                  <button onClick={() => void addDestination(item)}>
                    <Plus />
                    Lisää ja tarkista
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
      {tab === "packing" && <TripTacklePacking tripId={trip.id} />}
      {tab === "journal" && <TripFishingJournal tripId={trip.id} workspace={workspace} />}
      {tab === "tasks" && (
        <section className="trip-tasks">
          <header>
            <div>
              <span className="eyebrow">Valmistelut</span>
              <h3>Luvat, varusteet ja muistettavat asiat</h3>
            </div>
          </header>
          <div className="trip-task-list">
            {workspace.tasks.map((task) => (
              <article className={task.status} key={task.id}>
                <button
                  onClick={() => void toggleTask(task.id, task.status)}
                  aria-label={`${["completed", "verified", "user_reported"].includes(task.status) ? "Avaa" : "Merkitse tehdyksi"} ${task.title}`}
                >
                  {["completed", "verified", "user_reported"].includes(
                    task.status,
                  ) ? (
                    <CheckCircle2 />
                  ) : (
                    <Circle />
                  )}
                </button>
                <div>
                  <b>{task.title}</b>
                  <small>
                    {task.detail}
                    {task.status === "user_reported"
                      ? " Ilmoitettu tehdyksi – lupa ei ole vielä Core-varmennettu."
                      : ""}
                  </small>
                </div>
                <span>{task.source === "system" ? "Järjestelmä" : "Oma"}</span>
              </article>
            ))}
          </div>
          <form
            className="trip-task-form"
            onSubmit={(event) => void addTask(event)}
          >
            <select name="taskType">
              <option value="gear">Varuste</option>
              <option value="custom">Muu tehtävä</option>
            </select>
            <input
              name="title"
              maxLength={200}
              placeholder="Esimerkiksi pakkaa pelastusliivit"
              required
            />
            <button>
              <Plus />
              Lisää tehtävä
            </button>
          </form>
        </section>
      )}
    </section>
  );
}

export function ConsumerTrips({
  me,
  onChanged,
}: {
  me: ConsumerMe;
  onChanged: () => void;
}) {
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const requestedPlaceId = new URLSearchParams(window.location.search).get(
    "place",
  );
  const [open, setOpen] = useState(
    new URLSearchParams(window.location.search).get("new") === "1",
  );
  const [historical, setHistorical] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState(
      requestedPlaceId ?? me.savedPlaces[0]?.id ?? "",
    ),
    [startDate, setStartDate] = useState(tomorrow),
    [endDate, setEndDate] = useState(tomorrow),
    [activity, setActivity] = useState(me.profile.defaultActivity),
    [rodCount, setRodCount] = useState(me.profile.defaultRodCount),
    [targetSpecies, setTargetSpecies] = useState(""),
    [preferredStartTime, setPreferredStartTime] = useState("18:00"),
    [preferredEndTime, setPreferredEndTime] = useState("23:00"),
    [maxTravelMinutes, setMaxTravelMinutes] = useState(45);
  const selectedPlace =
    me.savedPlaces.find((place) => place.id === selectedPlaceId) ??
    me.savedPlaces[0];
  const durationDays = Math.max(
    1,
    Math.floor(
      (Date.parse(`${endDate}T12:00:00Z`) -
        Date.parse(`${startDate}T12:00:00Z`)) /
        86_400_000,
    ) + 1,
  );
  const setDuration = (days: number) =>
    setEndDate(
      new Date(Date.parse(`${startDate}T12:00:00Z`) + (days - 1) * 86_400_000)
        .toISOString()
        .slice(0, 10),
    );
  const dateSummary =
    startDate === endDate
      ? new Date(`${startDate}T12:00:00`).toLocaleDateString("fi-FI", {
          weekday: "short",
          day: "numeric",
          month: "long",
        })
      : `${new Date(`${startDate}T12:00:00`).toLocaleDateString("fi-FI", { day: "numeric", month: "numeric" })}–${new Date(`${endDate}T12:00:00`).toLocaleDateString("fi-FI", { day: "numeric", month: "numeric", year: "numeric" })}`;
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("Tallennetaan…");
    const data = new FormData(event.currentTarget);
    try {
      const created = await accountApi<{id:string}>("/api/account/trips", {
        method: "POST",
        body: JSON.stringify({
          savedPlaceId: data.get("savedPlaceId"),
          activity: data.get("activity"),
          rodCount: Number(data.get("rodCount")),
          plannedDate: data.get("plannedDate"),
          endDate: data.get("endDate"),
          targetSpecies: data.get("targetSpecies"),
          preferredStartTime: data.get("preferredStartTime"),
          preferredEndTime: data.get("preferredEndTime"),
          maxTravelMinutes: Number(data.get("maxTravelMinutes")),
          notes: data.get("notes"),
          historical,
        }),
      });
      setMessage("Kalareissu tallennettiin.");
      if (historical) {
        window.location.assign(`/oma/kalareissut/${encodeURIComponent(created.id)}?tab=journal&result=1`);
        return;
      }
      setOpen(false);
      onChanged();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Kalareissua ei voitu tallentaa.",
      );
    }
  };
  const remove = async (id: string) => {
    await accountApi(`/api/account/trips/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    onChanged();
  };
  const today = new Date().toISOString().slice(0, 10),
    planned = me.trips.filter(
      (trip) => trip.status === "planned" && trip.end_date >= today,
    ),
    awaitingCompletion = me.trips.filter(
      (trip) => trip.status === "planned" && trip.end_date < today,
    ),
    completed = me.trips
      .filter((trip) => trip.status === "completed")
      .sort((left, right) => right.end_date.localeCompare(left.end_date));
  return (
    <section className="account-panel consumer-trips" id="kalareissut">
      <div className="account-panel-heading">
        <div>
          <span className="eyebrow">Kalareissun suunnittelu</span>
          <h2>Tulevat reissut</h2>
        </div>
        <button
          className="secondary-action"
          type="button"
          onClick={() => { setHistorical(false); setOpen((value) => !value); }}
          disabled={!me.savedPlaces.length}
        >
          <CalendarDays size={16} />
          {open ? "Sulje" : "Suunnittele reissu"}
        </button>
      </div>
      {!me.savedPlaces.length && (
        <div className="consumer-inline-notice">
          <MapPin size={18} />
          <span>
            <b>Tallenna ensin kalapaikka</b>
            <small>
              Kalareissu liitetään järveen tai jokeen, jotta palaat oikeaan
              lupahakuun.
            </small>
          </span>
          <a href="/">
            Hae paikka <ArrowRight size={14} />
          </a>
        </div>
      )}
      {!open && me.savedPlaces.length > 0 && (
        <section className="trip-planner-callout">
          <div>
            <span className="eyebrow">
              Uusi: pidemmän reissun suunnittelija
            </span>
            <h3>Yksi päivä tai koko kuukauden reissu</h3>
            <p>
              Valitse alku ja loppu. Palvelu tarkistaa julkaistut luvat ja
              säännöt jokaiselle päivälle sekä seuraa sääennusteen avautumista.
            </p>
          </div>
          <div className="trip-planner-capabilities">
            <span>
              <ShieldCheck />
              Päiväkohtainen Core-tarkistus
            </span>
            <span>
              <CloudSun />
              Sää ja kausikuva erikseen
            </span>
            <span>
              <Map />
              Pääkohde ja varakohteet
            </span>
            <span>
              <ListChecks />
              Luvat, hankinnat ja tehtävät
            </span>
          </div>
          <button type="button" onClick={() => { setHistorical(false); setOpen(true); }}>
            <CalendarDays />
            Aloita 1–31 päivän suunnitelma <ArrowRight />
          </button>
          <button className="trip-history-entry" type="button" onClick={() => { const previous=new Date(Date.now()-86_400_000).toISOString().slice(0,10);setHistorical(true);setStartDate(previous);setEndDate(previous);setOpen(true); }}>
            <BookOpen />
            Kirjaa aiempi reissu
          </button>
        </section>
      )}
      {open && (
        <form className="consumer-trip-form" onSubmit={save}>
          <header className="trip-form-lead">
            <div>
              <span className="eyebrow">{historical ? "Aiempi reissu" : "Uusi reissu"}</span>
              <h3>{historical ? "Kirjaa jo päättynyt reissu" : "Rakenna reissusuunnitelma"}</h3>
              <p>
                {historical ? "Valitse paikka ja toteutunut ajankohta. Tallennuksen jälkeen lisäät tuloksen päiväkirjaan." : "Valitse kohde, ajankohta ja kalastustapa. Lopuksi voit tarkentaa, millaisen reissun haluat tehdä."}
              </p>
            </div>
            <ol aria-label="Suunnittelun vaiheet">
              <li className="active">
                <b>1</b>Kohde
              </li>
              <li>
                <b>2</b>Ajankohta
              </li>
              <li>
                <b>3</b>Tapa
              </li>
              <li>
                <b>4</b>Viimeistely
              </li>
            </ol>
          </header>
          <div className="trip-form-layout">
            <div className="trip-form-steps">
              <fieldset className="trip-form-step">
                <legend>
                  <b>1</b>
                  <span>
                    Valitse kalapaikka<small>Reissun pääkohde</small>
                  </span>
                </legend>
                <label>
                  Kalapaikka
                  <select
                    name="savedPlaceId"
                    value={selectedPlaceId}
                    onChange={(event) =>
                      setSelectedPlaceId(event.currentTarget.value)
                    }
                    required
                  >
                    {me.savedPlaces.map((place) => (
                      <option value={place.id} key={place.id}>
                        {place.name}
                        {place.municipality_name
                          ? ` · ${place.municipality_name}`
                          : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <p>
                  <MapPin />
                  Voit lisätä reissulle varakohteita tallennuksen jälkeen.
                </p>
              </fieldset>
              <fieldset className="trip-form-step">
                <legend>
                  <b>2</b>
                  <span>
                    Valitse ajankohta<small>1–31 päivää</small>
                  </span>
                </legend>
                <div className="trip-date-fields">
                  <label>
                    Alkaa
                    <input
                      name="plannedDate"
                      type="date"
                      min={historical ? new Date(Date.now()-31*86_400_000).toISOString().slice(0,10) : new Date().toISOString().slice(0, 10)}
                      max={historical ? new Date(Date.now()-86_400_000).toISOString().slice(0,10) : undefined}
                      value={startDate}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setStartDate(value);
                        if (endDate < value) setEndDate(value);
                      }}
                      required
                    />
                  </label>
                  <span>
                    <ArrowRight />
                  </span>
                  <label>
                    Päättyy
                    <input
                      name="endDate"
                      type="date"
                      min={startDate}
                      max={historical ? new Date(Date.now()-86_400_000).toISOString().slice(0,10) : undefined}
                      value={endDate}
                      onChange={(event) =>
                        setEndDate(event.currentTarget.value)
                      }
                      required
                    />
                  </label>
                </div>
                {!historical && <div
                  className="trip-duration-options"
                  aria-label="Reissun keston pikavalinnat"
                >
                  {[
                    [1, "Päivä"],
                    [3, "Pitkä viikonloppu"],
                    [7, "Viikko"],
                    [14, "2 viikkoa"],
                    [30, "Kuukausi"],
                  ].map(([days, label]) => (
                    <button
                      type="button"
                      className={durationDays === days ? "active" : undefined}
                      onClick={() => setDuration(Number(days))}
                      key={days}
                    >
                      {label}
                      <small>{days} pv</small>
                    </button>
                  ))}
                </div>}
                <p className="trip-duration-result">
                  <CalendarDays />
                  <b>
                    {durationDays} {durationDays === 1 ? "päivä" : "päivää"}
                  </b>
                  <span>{dateSummary}</span>
                </p>
              </fieldset>
              <fieldset className="trip-form-step">
                <legend>
                  <b>3</b>
                  <span>
                    Kalastustapa ja varusteet
                    <small>Core käyttää näitä lupa-arviossa</small>
                  </span>
                </legend>
                <div className="trip-method-fields">
                  <label>
                    Kalastustapa
                    <select
                      name="activity"
                      value={activity}
                      onChange={(event) =>
                        setActivity(
                          event.currentTarget.value as typeof activity,
                        )
                      }
                    >
                      {Object.entries(consumerActivityLabels).map(
                        ([value, label]) => (
                          <option value={value} key={value}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label>
                    Vapojen määrä
                    <input
                      name="rodCount"
                      type="number"
                      min="1"
                      max="20"
                      value={rodCount}
                      onChange={(event) =>
                        setRodCount(Number(event.currentTarget.value))
                      }
                      required
                    />
                  </label>
                </div>
              </fieldset>
              <fieldset className="trip-form-step trip-finalize-step">
                <legend>
                  <b>4</b>
                  <span>
                    Viimeistele reissu
                    <small>
                      Nämä valinnat tekevät säästä ja varakohteista
                      hyödyllisempiä
                    </small>
                  </span>
                </legend>
                <section className="trip-species-choice">
                  <header>
                    <FishSymbol />
                    <span>
                      <b>Mitä haluat kalastaa?</b>
                      <small>
                        Valinnainen – kohdelaji ei vaikuta lupapäätökseen
                      </small>
                    </span>
                  </header>
                  <div>
                    {["Ahven", "Kuha", "Hauki", "Taimen"].map((value) => (
                      <button
                        type="button"
                        className={
                          targetSpecies === value ? "active" : undefined
                        }
                        aria-pressed={targetSpecies === value}
                        onClick={() =>
                          setTargetSpecies((current) =>
                            current === value ? "" : value,
                          )
                        }
                        key={value}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                  <label>
                    <span>Muu kohdelaji</span>
                    <input
                      name="targetSpecies"
                      value={targetSpecies}
                      onChange={(event) =>
                        setTargetSpecies(event.currentTarget.value)
                      }
                      maxLength={80}
                      placeholder="Kirjoita laji"
                    />
                  </label>
                </section>
                <div className="trip-finalize-grid">
                  <section>
                    <header>
                      <Clock3 />
                      <span>
                        <b>Milloin aiot kalastaa?</b>
                        <small>Sää näytetään juuri tälle aikaikkunalle</small>
                      </span>
                    </header>
                    <div>
                      <label>
                        Alkaa
                        <input
                          name="preferredStartTime"
                          type="time"
                          value={preferredStartTime}
                          onChange={(event) =>
                            setPreferredStartTime(event.currentTarget.value)
                          }
                          required
                        />
                      </label>
                      <label>
                        Päättyy
                        <input
                          name="preferredEndTime"
                          type="time"
                          value={preferredEndTime}
                          onChange={(event) =>
                            setPreferredEndTime(event.currentTarget.value)
                          }
                          required
                        />
                      </label>
                    </div>
                  </section>
                  <section>
                    <header>
                      <CarFront />
                      <span>
                        <b>Kuinka kaukaa etsitään varakohteita?</b>
                        <small>Karkea hakuraja, ei navigaattorin ajoaika</small>
                      </span>
                    </header>
                    <label>
                      Enimmäismatka
                      <select
                        name="maxTravelMinutes"
                        value={maxTravelMinutes}
                        onChange={(event) =>
                          setMaxTravelMinutes(Number(event.currentTarget.value))
                        }
                      >
                        {[15, 30, 45, 60, 90].map((value) => (
                          <option value={value} key={value}>
                            Noin {value} min
                          </option>
                        ))}
                      </select>
                    </label>
                    <p>
                      Haku ulottuu noin {Math.round(maxTravelMinutes * 0.75)} km
                      linnuntietä pääkohteesta.
                    </p>
                  </section>
                </div>
                <label className="trip-packing-notes">
                  <span>
                    Muista nämä reissulla <small>Valinnainen</small>
                  </span>
                  <textarea
                    name="notes"
                    maxLength={500}
                    rows={3}
                    placeholder="Esimerkiksi lähtöpaikka, majoitus tai mukaan pakattava varuste"
                  />
                </label>
                <details className="trip-preference-boundary">
                  <summary>Mihin nämä valinnat vaikuttavat?</summary>
                  <p>
                    Kellonaika rajaa sääennustetta ja matkaraja varakohteita.
                    Kohdelaji tallennetaan suunnitelmaan, mutta palvelu ei väitä
                    vesistöä lajille sopivaksi ilman hyväksyttyä lähdetietoa.
                    Lupa ratkaistaan aina erikseen julkaistulla Corella.
                  </p>
                </details>
              </fieldset>
            </div>
            <aside className="trip-form-summary">
              <span className="eyebrow">Suunnitelman yhteenveto</span>
              <div className="trip-form-summary-place">
                <MapPin />
                <span>
                  <small>Pääkohde</small>
                  <b>{selectedPlace?.name ?? "Valitse paikka"}</b>
                  <em>{selectedPlace?.municipality_name ?? ""}</em>
                </span>
              </div>
              <dl>
                <div>
                  <dt>Ajankohta</dt>
                  <dd>{dateSummary}</dd>
                </div>
                <div>
                  <dt>Kesto</dt>
                  <dd>
                    {durationDays} {durationDays === 1 ? "päivä" : "päivää"}
                  </dd>
                </div>
                <div>
                  <dt>Kalastustapa</dt>
                  <dd>{consumerActivityLabels[activity]}</dd>
                </div>
                <div>
                  <dt>Vavat</dt>
                  <dd>{rodCount} kpl</dd>
                </div>
                <div>
                  <dt>Kohdelaji</dt>
                  <dd>{targetSpecies || "Ei valittu"}</dd>
                </div>
                <div>
                  <dt>Kalastusaika</dt>
                  <dd>
                    {preferredStartTime}–{preferredEndTime}
                  </dd>
                </div>
                <div>
                  <dt>Varakohteet</dt>
                  <dd>noin {maxTravelMinutes} min asti</dd>
                </div>
              </dl>
              <section>
                <ShieldCheck />
                <span>
                  <b>Tallennuksen jälkeen</b>
                  <small>
                    {historical ? "Reissu siirtyy tuloksen jälkikirjaukseen. Historiallista lupatilannetta ei päätellä ilman julkaistua aineistoa." : "Core tarkistaa luvat ja säännöt jokaiselle reissupäivälle. Sää ja varakohteet täydentävät suunnitelmaa."}
                  </small>
                </span>
              </section>
              {message && <p className="form-message">{message}</p>}
              <button>
                {historical ? "Tallenna ja kirjaa tulos" : "Tallenna ja tarkista reissu"} <ArrowRight />
              </button>
            </aside>
          </div>
        </form>
      )}
      {awaitingCompletion.length > 0 && (
        <section className="trip-awaiting-completion">
          <header>
            <div>
              <span className="eyebrow">Odottaa viimeistelyä</span>
              <h3>Päättyneet reissut</h3>
            </div>
            <small>{awaitingCompletion.length} viimeistelemättä</small>
          </header>
          <div>
            {awaitingCompletion.map((trip) => (
              <article key={trip.id}>
                <FishSymbol />
                <span>
                  <b>{trip.place_name}</b>
                  <small>
                    {new Date(`${trip.end_date}T12:00:00`).toLocaleDateString("fi-FI")} · {consumerActivityLabels[trip.activity]}
                  </small>
                </span>
                <a href={`/oma/kalareissut/${encodeURIComponent(trip.id)}?tab=journal&result=1`}>
                  Kirjaa tulos <ArrowRight />
                </a>
              </article>
            ))}
          </div>
        </section>
      )}
      {planned.length ? (
        <div className="consumer-trip-list">
          {planned.map((trip) => {
            const coreChanged = Boolean(
                me.activeCoreChecksum &&
                  trip.last_core_checksum &&
                  me.activeCoreChecksum !== trip.last_core_checksum,
              ),
              days =
                Math.floor(
                  (Date.parse(
                    `${trip.end_date || trip.planned_date}T12:00:00Z`,
                  ) -
                    Date.parse(`${trip.planned_date}T12:00:00Z`)) /
                    86_400_000,
                ) + 1;
            return (
              <article
                className={coreChanged ? "core-changed" : undefined}
                key={trip.id}
              >
                <span className="trip-date">
                  <b>
                    {new Date(
                      `${trip.planned_date}T00:00:00`,
                    ).toLocaleDateString("fi-FI", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </b>
                  <small>
                    {trip.end_date && trip.end_date !== trip.planned_date
                      ? `– ${new Date(`${trip.end_date}T00:00:00`).toLocaleDateString("fi-FI", { day: "2-digit", month: "short" })}`
                      : new Date(
                          `${trip.planned_date}T00:00:00`,
                        ).toLocaleDateString("fi-FI", { weekday: "short" })}
                  </small>
                </span>
                <div>
                  <div className="trip-list-title">
                    <h3>{trip.place_name}</h3>
                    <span>
                      {days} {days === 1 ? "päivä" : "päivää"}
                    </span>
                  </div>
                  <p>
                    {trip.municipality_name ?? "Kunta ei tiedossa"} ·{" "}
                    {consumerActivityLabels[trip.activity]} · {trip.rod_count}{" "}
                    vavalla
                  </p>
                  {(trip.target_species ||
                    trip.preferred_start_time ||
                    trip.max_travel_minutes) && (
                    <div className="trip-list-preferences">
                      {trip.target_species && (
                        <span>
                          <FishSymbol /> {trip.target_species}
                        </span>
                      )}
                      {trip.preferred_start_time && trip.preferred_end_time && (
                        <span>
                          <Clock3 /> {trip.preferred_start_time}–
                          {trip.preferred_end_time}
                        </span>
                      )}
                      {trip.max_travel_minutes && (
                        <span>
                          <CarFront /> {trip.max_travel_minutes} min
                        </span>
                      )}
                    </div>
                  )}
                  {days === 1 && (
                    <a
                      className="trip-expand-link"
                      href={`/oma/kalareissut/${encodeURIComponent(trip.id)}?edit=1`}
                    >
                      <CalendarDays />
                      Laajenna pidemmäksi reissuksi
                    </a>
                  )}
                  {coreChanged && (
                    <span className="trip-core-change">
                      <AlertTriangle size={13} /> Core on päivittynyt –
                      automaattinen tarkistus on jonossa
                    </span>
                  )}
                  {trip.automation_status === "retry" && (
                    <span className="trip-core-change">
                      <AlertTriangle size={13} /> Automaattinen tarkistus
                      yritetään uudelleen
                    </span>
                  )}
                  {trip.notes && <small>{trip.notes}</small>}
                </div>
                <div className="trip-actions">
                  <a href={`/oma/kalareissut/${encodeURIComponent(trip.id)}`}>
                    Avaa suunnitelma
                  </a>
                  <a
                    href={`/oma/kalareissut/${encodeURIComponent(trip.id)}?edit=1`}
                  >
                    <Pencil size={14} />
                    Muokkaa
                  </a>
                  <a href={`/oma/kalareissut/${encodeURIComponent(trip.id)}`}>
                    <Check size={14} /> Viimeistele
                  </a>
                  <button
                    aria-label={`Poista kalareissu ${trip.place_name}`}
                    onClick={() => void remove(trip.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : me.savedPlaces.length ? (
        <div className="account-empty compact">
          <CalendarDays />
          <p>
            Ei vielä suunniteltuja reissuja. Lisää ajankohta, paikka ja
            kalastustapa.
          </p>
        </div>
      ) : null}
      {completed.length > 0 && (
        <section className="trip-history">
          <header>
            <div>
              <span className="eyebrow">Oma reissuhistoria</span>
              <h3>Valmiit reissut</h3>
            </div>
            <a href="/oma/saaliit">Näytä kaikki saaliit <ArrowRight /></a>
          </header>
          <div>
            {completed.map((trip) => (
              <article key={trip.id}>
                <CheckCircle2 />
                <span>
                  <b>{trip.place_name}</b>
                  <small>
                    {new Date(`${trip.end_date}T12:00:00`).toLocaleDateString("fi-FI")} · {consumerActivityLabels[trip.activity]}
                  </small>
                </span>
                <a href={`/oma/kalareissut/${encodeURIComponent(trip.id)}`}>
                  Avaa päiväkirja
                </a>
                <button
                  aria-label={`Poista valmis kalareissu ${trip.place_name}`}
                  onClick={() => void remove(trip.id)}
                >
                  <Trash2 />
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
    </section>
  );
}
